import { Box, Grid } from '@factorialco/deprecated-design-system'
import {
  TrainingInsightFragment,
  TrainingWithClassFragment,
  TrainingsDevLmsFeatureFlagDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'

import { AverageChart } from 'modules/trainings/components/Insights/AverageChart'
import { BarChart } from 'modules/trainings/components/Insights/BarChart'
import { DataList } from 'modules/trainings/components/Insights/DataList'
import { ListChart } from 'modules/trainings/components/Insights/ListChart'
import { PieChart } from 'modules/trainings/components/Insights/PieChart'
import { TextChart } from 'modules/trainings/components/Insights/TextChart'
import { useAttendancePct } from 'modules/trainings/hooks/Insights/useAttendancePct'
import { useMore40hs } from 'modules/trainings/hooks/Insights/useMore40hs'
import { useTrainedEmployeesByGender } from 'modules/trainings/hooks/Insights/useTrainedEmployeesByGender'
import { useTrainedEmployeesByTeam } from 'modules/trainings/hooks/Insights/useTrainedEmployeesByTeam'
import { useTrainingHours } from 'modules/trainings/hooks/Insights/useTrainingHours'
import { useTrainingHoursAndCost } from 'modules/trainings/hooks/Insights/useTrainingHoursAndCost'
import { useTrainingHoursEmployee } from 'modules/trainings/hooks/Insights/useTrainingHoursEmployee'
import { useTrainingsEmployees } from 'modules/trainings/hooks/Insights/useTrainingsEmployees'

interface Props {
  insights: TrainingInsightFragment
  trainings: TrainingWithClassFragment[]
}

export const Body = ({ insights, trainings }: Props) => {
  const { totalTrainings, totalCost, salaryCost, subsidizedCost } =
    useTrainingHoursAndCost(insights)
  const { totalUniqueEmployees, averageEmployeesPerTraining } = useTrainingsEmployees(insights)
  const { trainedEmployeesByTeam } = useTrainedEmployeesByTeam(insights)
  const { trainedEmployeesByGender } = useTrainedEmployeesByGender(insights)
  const { trainingHoursEmployee } = useTrainingHoursEmployee(insights)
  const { totalHours, hours } = useTrainingHours(insights)
  const { attendance } = useAttendancePct(insights)
  const { more40hs } = useMore40hs(insights)

  const {
    company: { id, locale, legalEntities },
  } = useCurrentSession()
  const mainLegalEntityCurrency = legalEntities.find((legalEntity) => legalEntity.main)?.currency

  const { data: featureFlagData } = useQuery(TrainingsDevLmsFeatureFlagDocument, {
    variables: { companyId: id },
  })
  const hasDuration = Boolean(
    featureFlagData?.features?.featuresConnection?.nodes?.find(
      (n) => n.name === 'dev_training_lms' && n.enabled == true
    )
  )
  const getTrainingDuration = (trainings: TrainingWithClassFragment[]) => {
    const trainingDurationData = compact(
      trainings.map((training) => {
        return {
          label: training.name,
          value: training.totalDuration ? training.totalDuration : 0,
        }
      })
    )

    return { trainingDurationData }
  }
  const { trainingDurationData } = getTrainingDuration(trainings)

  return (
    <>
      <Grid variant="fixed">
        <Box gridColSpan={{ xxs: 3 }}>
          <TextChart
            title={i18n.t('trainings.insights.charts.courses_number.title')}
            info={i18n.t('trainings.insights.charts.courses_number.tooltip')}
            content={String(totalTrainings)}
          />
        </Box>
        <Box gridColSpan={{ xxs: 3 }}>
          <TextChart
            title={i18n.t('trainings.insights.charts.total_cost.title')}
            info={i18n.t('trainings.insights.charts.total_cost.tooltip')}
            content={MoneyWithCurrency({
              optionalDecimals: true,
              amount: parseFloat((Number(totalCost) * 100).toFixed(0)),
              locale: locale || '',
              currency: isCurrency(mainLegalEntityCurrency) ? mainLegalEntityCurrency : undefined,
            })}
          />
        </Box>
        <Box gridColSpan={{ xxs: 3 }}>
          <TextChart
            title={i18n.t('trainings.insights.charts.salary_cost.title')}
            info={i18n.t('trainings.insights.charts.salary_cost.tooltip')}
            content={MoneyWithCurrency({
              optionalDecimals: true,
              amount: parseFloat((Number(salaryCost) * 100).toFixed()),
              locale: locale || '',
              currency: isCurrency(mainLegalEntityCurrency) ? mainLegalEntityCurrency : undefined,
            })}
          />
        </Box>
        <Box gridColSpan={{ xxs: 3 }}>
          <TextChart
            title={i18n.t('trainings.insights.charts.total_subsidized_cost.title')}
            info={i18n.t('trainings.insights.charts.total_subsidized_cost.tooltip')}
            content={MoneyWithCurrency({
              optionalDecimals: true,
              amount: parseFloat((Number(subsidizedCost) * 100).toFixed()),
              locale: locale || '',
              currency: isCurrency(mainLegalEntityCurrency) ? mainLegalEntityCurrency : undefined,
            })}
          />
        </Box>
        <Box gridColSpan={{ xxs: 12 }} gridRowSpan={10}>
          <AverageChart
            title={i18n.t('trainings.insights.charts.employees.title')}
            info={i18n.t('trainings.insights.charts.employees.tooltip')}
            averageTitle={i18n.t('trainings.insights.charts.employees.average_title')}
            contentTitle={i18n.t('trainings.insights.charts.employees.content_title')}
            averageContent={String(averageEmployeesPerTraining)}
            content={String(totalUniqueEmployees)}
          />
        </Box>
        <Box gridColSpan={{ xxs: 6 }}>
          <BarChart
            values={trainedEmployeesByTeam}
            title={i18n.t('trainings.insights.charts.teams.title')}
            info={i18n.t('trainings.insights.charts.teams.tooltip')}
          />
        </Box>
        <Box gridColSpan={{ xxs: 6 }}>
          <PieChart
            data={trainedEmployeesByGender}
            title={i18n.t('trainings.insights.charts.employees_gender.title')}
            info={i18n.t('trainings.insights.charts.employees_gender.tooltip')}
            subtitle={i18n.t('trainings.insights.charts.employees_gender.subtitle')}
          />
        </Box>
        <Box gridColSpan={{ xxs: 12 }} gridRowSpan={10}>
          <AverageChart
            title={i18n.t('trainings.insights.charts.training_hours.title')}
            info={i18n.t('trainings.insights.charts.training_hours.tooltip')}
            averageTitle={i18n.t('trainings.insights.charts.training_hours.average_title')}
            contentTitle={i18n.t('trainings.insights.charts.training_hours.content_title')}
            averageContent={String(hours)}
            content={String(totalHours)}
          />
        </Box>
        {hasDuration && (
          <Box gridColSpan={{ xxs: 12 }} gridRowSpan={10}>
            <DataList
              data={trainingDurationData}
              title={i18n.t('trainings.insights.charts.training_duration.title')}
              subtitle={i18n.t('trainings.insights.charts.training_duration.subtitle')}
              valueTitle={i18n.t('trainings.insights.charts.training_duration.value_title')}
              info={i18n.t('trainings.insights.charts.training_duration.tooltip')}
              formatValue={(value) => {
                const hours = Math.floor(value)
                const minutes = Math.round((value % 1) * 60)
                return `${hours}h ${minutes}m`
              }}
            />
          </Box>
        )}
        <Box gridColSpan={{ xxs: 12 }}>
          <ListChart
            data={trainingHoursEmployee}
            title={i18n.t('trainings.insights.charts.training_hours_employee.title')}
            subtitle={i18n.t('trainings.insights.charts.training_hours_employee.subtitle')}
            valueTitle={i18n.t('trainings.insights.charts.training_hours_employee.value_title')}
            info={i18n.t('trainings.insights.charts.training_hours_employee.tooltip')}
          />
        </Box>
        <Box gridColSpan={{ xxs: 6 }}>
          <PieChart
            data={more40hs}
            title={i18n.t('trainings.insights.charts.more_40hs.title')}
            info={i18n.t('trainings.insights.charts.more_40hs.tooltip')}
            customMetric={more40hs[0]?.value}
          />
        </Box>
        <Box gridColSpan={{ xxs: 6 }}>
          <PieChart
            title={i18n.t('trainings.insights.charts.average_attendance.title')}
            info={i18n.t('trainings.insights.charts.average_attendance.tooltip')}
            data={attendance}
            hideCustomMetric
            legendType="percentage"
          />
        </Box>
      </Grid>
    </>
  )
}
